import { Sparkles, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

interface PostResultsUpgradeBannerProps {
  isPremium: boolean;
  onUpgrade: () => void;
  context?: "search" | "recommendations" | "discovery";
}

const COPY: Record<string, { title: string; subtitle: string }> = {
  search: {
    title: "Want more results like these?",
    subtitle: "VIP unlocks 100 smart searches/month, TV shows, and advanced filters.",
  },
  recommendations: {
    title: "Like these picks?",
    subtitle: "VIP recommendations dig deeper — niche gems, recent releases, and reasoning you can trust.",
  },
  discovery: {
    title: "Found something good?",
    subtitle: "Swiftix VIP adds five-mood blends, unlimited genres, deeper Vault picks, and free rerolls.",
  },
};

const DISMISS_KEY = "post_results_banner_dismissed_at";

export const PostResultsUpgradeBanner = ({
  isPremium,
  onUpgrade,
  context = "search",
}: PostResultsUpgradeBannerProps) => {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (isPremium) return;
    try {
      const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
      // Re-show after 24 hours since last dismissal
      const ONE_DAY = 24 * 60 * 60 * 1000;
      setDismissed(Date.now() - last < ONE_DAY);
    } catch {
      setDismissed(false);
    }
  }, [isPremium]);

  if (isPremium || dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  const copy = COPY[context];

  return (
    <div className="relative rounded-xl border border-accent/30 bg-gradient-to-r from-accent/5 via-primary/5 to-secondary/5 p-4 sm:p-5">
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pr-6">
        <div className="flex-shrink-0 p-2 rounded-lg bg-gradient-to-br from-accent to-primary">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm sm:text-base">{copy.title}</p>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{copy.subtitle}</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onUpgrade}
          className="border-accent/40 hover:bg-accent/10 self-start sm:self-auto"
        >
          See VIP
          <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </div>
    </div>
  );
};
